import { isEmpty } from 'lodash';
import { stringify } from 'query-string';
import { HttpClient } from 'api/http-client';
import { User } from 'api/models';
import { StatusUpdate } from 'api/status-updates';
import { CustomerCompanyListFilter } from 'components/organisms/customer-portfolio/company-list/url-params/types';
import { OrgNodeApi } from 'components/organisms/customer-portfolio/org-chart/types';
import { Item } from 'components/organisms/kanban-board/group-by';
import {
  DateRange,
  getFormattedValueFromDateRange,
  Range,
} from 'components/organisms/projects-report/common/range-picker/types';
import { SourceType } from 'components/organisms/tasks/fields';
import { fieldAccessMetaToBase3 } from 'utils/field-access';
import {
  ApiFilter,
  customFieldKeyToString,
} from 'utils/simple-search-filter/simple-search-filter';
import { Activity } from './activity-type';
import {
  PeopleOptionOrgChart,
  CustomerCompany,
  SwimlanesWithMeta,
  GetSwimlaneLayoutParams,
  DragAndDropCompanyParams,
  DragAndDropCompany,
  Swimlane,
  GetCompainesInSwimlaneColumnParams,
  Swimlanes,
  GetCompainesInSwimlanesParams,
  CreateCustomerCompanyPayload,
  PaginatedCustomerCompaniesParams,
  PaginatedCustomerCompaniesResponse,
  UpdateCustomerCompanyPayload,
  GetActivitesParams,
  GetActivitesCountParams,
  ActivitiesTimelineMeta,
  CustomerCompanyAPIType,
} from './types';

const dateRangeToQueryString = (range: DateRange): [string, string] => {
  if (range.type === Range.Overall) {
    return ['', ''];
  }

  return getFormattedValueFromDateRange(range);
};

const filterToQueryString = (filter: CustomerCompanyListFilter): string => {
  const criteriaQuery = ApiFilter.toQueryStringFormat(filter.criteria);
  // @TODO: @kadhirr, please bring this back after time range is reimplemented
  // const [startDate, endDate] = dateRangeToQueryString(filter.range);

  return `${criteriaQuery}&${stringify(
    { match: criteriaQuery.length ? filter.type : null },
    { skipNull: true, skipEmptyString: true },
  )}`;
};

const searchTextToQueryString = (searchText: string) =>
  isEmpty(searchText) ? '' : `&company*companyName.contains=${searchText}`;
const searchAndFilterToQueryString = (
  filter: CustomerCompanyListFilter,
  searchText: string,
): string => filterToQueryString(filter) + searchTextToQueryString(searchText);
const groupByToString = (field: Item) => {
  return !['companyStage', 'companyHealth'].includes(field.value) &&
    (field.isCustomField || field.isUserField)
    ? customFieldKeyToString({
        fieldId: field.fieldId,
        sourceType: SourceType.Company,
      })
    : field.value;
};

class CustomerCompanyHttpClient extends HttpClient {
  public createCompany = (
    payload: CreateCustomerCompanyPayload,
  ): Promise<CustomerCompany> => {
    return this.post('/companies', payload);
  };

  public updateCompany = (
    companyId: number,
    payload: UpdateCustomerCompanyPayload,
  ): Promise<CustomerCompany> => {
    return this.put(`/companies/${companyId}`, payload);
  };

  public getCompanyById = async (
    companyId: number,
    filter?: CustomerCompanyListFilter,
  ): Promise<CustomerCompany> => {
    const [startDate, endDate] = filter
      ? dateRangeToQueryString(filter.range)
      : ['', ''];

    return this.get<CustomerCompanyAPIType>(
      `/companies/${companyId}/customer-portfolio?${stringify(
        { startDate, endDate },
        { skipNull: true, skipEmptyString: true },
      )}`,
    ).then((response) => ({
      ...response,
      fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
        response.fieldsAccessMeta ?? {},
      ),
    }));
  };

  public getStatusUpdatesByCompanyId = async (
    companyId: number,
  ): Promise<StatusUpdate[]> => {
    return this.get<StatusUpdate[]>(`status-updates/companies/${companyId}`);
  };

  public getLayout = async ({
    filter,
    columnGroup,
    searchText,
  }: GetSwimlaneLayoutParams): Promise<SwimlanesWithMeta> =>
    this.get<SwimlanesWithMeta>(
      `/companies/getlightkanbanCount?${searchAndFilterToQueryString(
        filter,
        searchText,
      )}`,
      {
        params: {
          columnGroup: groupByToString(columnGroup),
        },
      },
    );

  public getCompaniesInSwimlanes = ({
    filter,
    searchText,
    columnGroup,
    swimlaneIds,
  }: GetCompainesInSwimlanesParams): Promise<Swimlanes> =>
    this.get<{
      kanbanResponseModelList: Swimlanes;
    }>(
      `companies/getlightkanbanV1?${searchAndFilterToQueryString(
        filter,
        searchText,
      )}`,
      {
        params: {
          rowIds: swimlaneIds.join(','),
          columnGroup: groupByToString(columnGroup),
        },
      },
    ).then((response) => response.kanbanResponseModelList);

  public getCompainesInSwimlaneColumn = ({
    filter,
    searchText,
    offsetCol,
    columnGroup,
    colId,
    rowId,
  }: GetCompainesInSwimlaneColumnParams): Promise<Swimlane> =>
    this.get<Swimlane>(
      `companies/getlightkanbanColV1?${searchAndFilterToQueryString(
        filter,
        searchText,
      )}`,
      {
        params: {
          columnGroup: groupByToString(columnGroup),
          colId,
          rowId,
          offsetCol,
          limitCol: 10,
        },
      },
    );

  public dragAndDropCompany = ({
    columnGroup,
    filter,
    searchText,
    fromRowId,
    fromColId,
    toRowId,
    toColId,
    companyId,
  }: DragAndDropCompanyParams): Promise<DragAndDropCompany> =>
    this.get<DragAndDropCompany>(
      `/companies/getDragAndDropAllowed?${searchAndFilterToQueryString(
        filter,
        searchText,
      )}`,
      {
        params: {
          columnGroup: groupByToString(columnGroup),
          fromRowId,
          fromColId,
          toRowId,
          toColId,
          companyId,
          rowGroup: 'company',
        },
      },
    );

  public getValidManagerOfOptions = async (
    userId: number,
  ): Promise<PeopleOptionOrgChart[]> => {
    return this.get(`/users/valid-reportee/${userId}`);
  };

  public getValidReportingToOptions = async (
    userId: number,
  ): Promise<PeopleOptionOrgChart[]> => {
    return this.get(`/users/valid-managers/${userId}`);
  };

  public getPeopleHierarchy = async (
    companyId: number,
  ): Promise<OrgNodeApi[]> => {
    return this.get(`/companies/${companyId}/hierarchy`);
  };

  public getCompanyPeopleList = async (companyId: number): Promise<User[]> => {
    return this.get(`/companies/${companyId}/people`);
  };

  public getPaginatedCompanies = async ({
    filter,
    searchText,
    sort,
    startRow,
    endRow,
  }: PaginatedCustomerCompaniesParams): Promise<PaginatedCustomerCompaniesResponse> => {
    return this.get<PaginatedCustomerCompaniesResponse>(
      `/companies/searchlightV1?${searchAndFilterToQueryString(
        filter,
        searchText,
      )}`,
      {
        params: {
          sort,
          offset: startRow,
          limit: endRow - startRow,
        },
      },
    );
  };

  public getCompanyActivites = async ({
    endDate,
    limit,
    lastActivityKey,
    companyId,
  }: GetActivitesParams): Promise<Activity[]> =>
    this.get<Activity[]>('/activities', {
      params: {
        endDate,
        offset: lastActivityKey,
        limit,
        filter: 'MONTH',
        sourceId: companyId,
        sourceType: 'COMPANY',
      },
    });

  public getCompanyActivitiesCount = async ({
    companyId,
    yearsToBeFetched: years,
  }: GetActivitesCountParams): Promise<ActivitiesTimelineMeta> =>
    this.get<ActivitiesTimelineMeta>('/activities/count', {
      params: {
        sourceId: companyId,
        sourceType: 'COMPANY',
        period: years.join(','),
      },
    });

  public deleteCustomerCompany = async (companyId: number): Promise<void> => {
    return this.delete(`/companies/${companyId}`);
  };

  public mergeCompanies = async (
    sourceCompanyIds: number[],
    destinationCompanyId: number,
  ) => {
    return this.put(
      '/companies/merge',
      {},
      {
        params: {
          childCompanyIds: encodeURI(sourceCompanyIds.toString()),
          parentCompanyId: destinationCompanyId,
        },
      },
    );
  };

  public async getAllCustomerCompanies(): Promise<CustomerCompany[]> {
    return this.get<CustomerCompany[]>('/companies', {
      params: { companyType: 'CUSTOMER' },
    });
  }
}
export const CustomerCompanyAPI = new CustomerCompanyHttpClient();
